import React from 'react';
import {
  Plane, Car, CarFront, KeyRound, Utensils, Users, Mic, Ticket, ConciergeBell, StickyNote as MemoIcon,
  MapPin, Phone, ArrowRight, PlaneLanding, X,
} from 'lucide-react';
import { hotelGuestNames, describeStop } from './HotelStayCards';
import { displayStatus } from '../../utils/componentStatus';
import { ClockTime } from './TimelineCardVisuals';
import {
  MONO, wallClock, formatClock, formatDay, durationLabel, present, Pill, StatusPill, CardShell, CardHeader, RouteLine,
  TimeBlock, Label, DetailGrid, DetailText, Chips, CardFooter, InsideNotes, TransferBooked, NoRideStrip, InfoStrip,
  RideSection, ButtonLink,
} from './TimelineCardKit';

// Trip timeline "Cards" view: one designed card per component type, in the
// same visual language as the hotel stay cards. Hotels live in HotelStayCards.

// The component types (and split events) that get a designed card.
export const tripCardKind = (component) => {
  if (!component || component.is_placeholder) return null;
  const type = component.originalComponent?.component_type || component.component_type;
  if (component.isPrivateFlightLeg) return 'private_flight';
  if (component.isRentalCarEvent) return component.eventType === 'return' ? 'rental_return' : 'rental_pickup';
  if (['chauffeur_service', 'restaurant', 'meeting', 'speaker_meeting', 'event', 'airport_vip_service', 'memo'].includes(type)) return type;
  return null;
};

const dataOf = (component) => component.originalComponent?.component_data || component.component_data || {};

const minutesOf = (raw) => {
  const w = wallClock(raw);
  return w?.time ? Date.parse(`${w.date}T${w.time}:00Z`) / 60000 : null;
};

/**
 * The Urbanride ride linked to this item that takes the guest to it ('to') or
 * away from it ('from'), judged by when the ride picks up against the item's
 * own start and end. Private flight legs only count rides booked for that leg.
 */
export const findLinkedRide = (component, direction) => {
  const day = wallClock(component.start_datetime)?.date;
  const start = minutesOf(component.start_datetime);
  if (!day || start === null) return null;
  const end = minutesOf(component.end_datetime) ?? start;
  const rides = (dataOf(component).urbanride_bookings || []).filter((booking) => {
    if (component.isPrivateFlightLeg && Array.isArray(booking.leg_indices) && booking.leg_indices.length
      && !booking.leg_indices.includes(component.legIndex)) return false;
    return minutesOf(booking.pickup_datetime) !== null;
  });
  if (direction === 'to') {
    return rides
      .filter(b => wallClock(b.pickup_datetime).date === day && minutesOf(b.pickup_datetime) <= start)
      .sort((a, b) => minutesOf(b.pickup_datetime) - minutesOf(a.pickup_datetime))[0] || null;
  }
  return rides
    .filter(b => minutesOf(b.pickup_datetime) >= end && minutesOf(b.pickup_datetime) - end < 24 * 60)
    .sort((a, b) => minutesOf(a.pickup_datetime) - minutesOf(b.pickup_datetime))[0] || null;
};

// People on an item as [{ name, emphasis }], trip executives emphasised.
const peopleOn = (cd, trip, listKey) => {
  const listed = (cd[listKey] || []).map(p => (typeof p === 'string' ? p : p?.name)).filter(Boolean);
  const execNames = new Set((trip?.executives || []).map(e => `${e.first_name} ${e.last_name}`));
  const guests = hotelGuestNames(cd, trip).map(n => (typeof n === 'string' ? { name: n, emphasis: execNames.has(n) } : n));
  const seen = new Set(guests.map(g => g.name));
  const primary = cd.primary_executive_id && (trip?.executives || []).find(e => e.id === cd.primary_executive_id);
  const out = [];
  if (primary) {
    const name = primary.full_name || `${primary.first_name} ${primary.last_name}`;
    if (!seen.has(name)) out.push({ name, emphasis: true });
  }
  out.push(...guests);
  listed.forEach((name) => { if (!seen.has(name) && !out.some(p => p.name === name)) out.push({ name, emphasis: execNames.has(name) }); });
  return out;
};

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

const mapsHref = (query) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const Directions = ({ query }) => (
  <dd className="m-0 mt-1">
    <a href={mapsHref(query)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-ui-link hover:text-ui-blue-fg">
      <MapPin className="w-3 h-3" aria-hidden="true" />Directions
    </a>
  </dd>
);

const PhoneLink = ({ phone, className = 'text-xs' }) => (present(phone) ? (
  <a href={`tel:${String(phone).replace(/[^\d+]/g, '')}`} onClick={e => e.stopPropagation()} className={`${className} text-ui-link hover:text-ui-blue-fg`}>{phone}</a>
) : null);

const DateLine = ({ raw, withYear = true }) => {
  const date = wallClock(raw)?.date;
  return date ? <div className="text-[13px] text-ui-ink-2 mt-0.5">{formatDay(date, withYear)}</div> : null;
};

// Ride strips: a booked ride shows green; otherwise offer to book one.
const RideTo = ({ ride, bookedTitle, place, fromStop, onBook }) => {
  if (ride) return <TransferBooked booking={ride} title={bookedTitle} />;
  if (!onBook) return null;
  return <NoRideStrip text={`No ride to ${place} yet${fromStop ? `, from ${fromStop}` : ''}.`} onBook={onBook} />;
};

const RideFrom = ({ ride, bookedTitle, place, toStop, onBook }) => {
  if (ride) return <TransferBooked booking={ride} title={bookedTitle} />;
  if (!onBook) return null;
  return <NoRideStrip text={`No ride from ${place} yet${toStop ? `, to ${toStop}` : ''}.`} onBook={onBook} />;
};

const Footer = ({ p, noun, confNumber, confLabel, ...rest }) => (
  <CardFooter
    component={p.component}
    canViewTrip={p.canViewTrip}
    confNumber={confNumber}
    confLabel={confLabel}
    isConfidential={p.isConfidential}
    onOpenConfidentiality={p.onOpenConfidentiality}
    onEdit={p.onEdit}
    editLabel={`Edit ${noun}`}
    onDelete={p.onDelete ? () => p.onDelete(p.component.originalComponent || p.component) : undefined}
    deleteLabel={`Delete ${noun}`}
    actions={p.footerActions}
    {...rest}
  />
);

// `details` (Full view's extra sections) sit above the card's notes and footer.
const Notes = ({ p }) => (
  <>
    {p.details && <div className="px-4 sm:px-[22px] pb-4">{p.details}</div>}
    <InsideNotes notes={p.insideNotes} noteHandlers={p.noteHandlers} currentUserId={p.currentUserId} />
  </>
);

// ── Private flight (one card per leg) ─────────────────────────────────────
const PrivateFlightCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = dataOf(component);
  const leg = component.flightLeg || {};
  const depName = present(leg.departure_airport_name || leg.departure_airport_display || leg.departure_airport) || 'TBD';
  const arrName = present(leg.arrival_airport_name || leg.arrival_airport_display || leg.arrival_airport) || 'TBD';
  const depCode = leg.departure_airport_iata || leg.iata_code || leg.departure_airport_code?.slice(-3) || leg.departure_airport_icao?.slice(-3) || '';
  const arrCode = leg.arrival_airport_iata || leg.arrival_iata_code || leg.arrival_airport_code?.slice(-3) || leg.arrival_airport_icao?.slice(-3) || '';
  const depFbo = present(leg.departure_fbo_name || leg.departure_fbo);
  const arrFbo = present(leg.arrival_fbo_name || leg.arrival_fbo);
  const tail = present(leg.tail_number || leg.tail || cd.tail_number || cd.tail || cd.aircraft_tail);
  const operatorType = present(cd.operator_type);
  const people = peopleOn(cd, trip, 'passengers');
  const End = ({ code, name, fbo, raw, right }) => (
    <div className={`min-w-0 ${right ? 'text-right' : ''}`}>
      <div className="text-[28px] font-bold tracking-tight text-ui-ink">{code || '—'}</div>
      <div className="text-[13px] text-ui-ink-3 break-words">{name}</div>
      {fbo && <div className="text-xs text-ui-amber-fg mt-0.5 break-words">{fbo}</div>}
      <div className="text-[22px] sm:text-2xl font-semibold text-ui-ink mt-2.5" style={MONO}>
        <TimeText raw={raw} prefs={prefs} />
      </div>
    </div>
  );

  return (
    <CardShell label={`Private flight: ${depName} to ${arrName}`}>
      <CardHeader Icon={Plane} tone="violet" title={`${depName} to ${arrName}`} subtitle={`Private flight · ${formatDay(wallClock(component.start_datetime)?.date, true)}`}>
        <StatusPill status={displayStatus(component)} />
        {tail && <span className="text-[13px] font-semibold px-2 py-1 rounded-md bg-ui-violet-bg text-ui-violet-fg" style={MONO}>{tail}</span>}
      </CardHeader>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-4 px-4 sm:px-[22px] py-5">
        <End code={depCode} name={depName} fbo={depFbo} raw={component.start_datetime} />
        <RouteLine tone="violet" Icon={Plane} iconClassName="rotate-45" caption={durationLabel(component.start_datetime, component.end_datetime)} />
        <End code={arrCode} name={arrName} fbo={arrFbo} raw={component.end_datetime} right />
      </div>
      <RideSection>
        <RideTo ride={p.rideTo} bookedTitle="Ride to the FBO booked" place={depFbo || depName} fromStop={p.prevStop?.title} onBook={p.onBookTo} />
        <RideFrom ride={p.rideFrom} bookedTitle="Ride from the FBO booked" place={arrFbo || arrName} toStop={p.nextStop?.title} onBook={p.onBookFrom} />
      </RideSection>
      <DetailGrid items={[
        present(cd.charter_operator_name) && { term: 'Operator', children: <><DetailText>{cd.charter_operator_name}</DetailText>{present(cd.charter_operator_phone) && <dd className="m-0 mt-0.5"><PhoneLink phone={cd.charter_operator_phone} /></dd>}</> },
        (present(cd.aircraft_type) || operatorType) && { term: 'Aircraft', children: <>{present(cd.aircraft_type) && <DetailText>{cd.aircraft_type}</DetailText>}{operatorType && <DetailText soft>{operatorType.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</DetailText>}</> },
        people.length > 0 && { term: people.length === 1 ? 'Passenger' : 'Passengers', children: <PeopleLines people={people} /> },
      ]} />
      <Notes p={p} />
      <Footer p={p} noun="private flight" confNumber={present(cd.confirmation_number || component.confirmation_number)} />
    </CardShell>
  );
};

const TimeText = ({ raw, prefs }) => <ClockTime value={formatClock(wallClock(raw)?.time, prefs)} />;

// First person bold, the next on a second line, the rest as "+N".
const PeopleLines = ({ people }) => (
  <>
    <DetailText strong>{people[0].name}</DetailText>
    {people.length > 1 && <DetailText soft>{people[1].name}{people.length > 2 ? `, +${people.length - 2}` : ''}</DetailText>}
  </>
);

// ── Chauffeured transfer ──────────────────────────────────────────────────
const RIDE_STEPS = [
  { label: 'Confirmed', done: cd => !!(cd.urbanride_urcn || cd.urbanride_status), at: () => null },
  { label: 'Chauffeur assigned', done: cd => cd.chauffeur_status === 'CA' || !!cd.vehicle_status, at: cd => cd.chauffeur_assigned_at },
  { label: 'On location', done: cd => ['VOL', 'POB', 'RC'].includes(cd.vehicle_status), at: cd => cd.vehicle_vol_at },
  { label: 'Passenger on board', done: cd => ['POB', 'RC'].includes(cd.vehicle_status), at: cd => cd.vehicle_pob_at },
  { label: 'Dropped off', done: cd => cd.vehicle_status === 'RC', at: cd => cd.vehicle_rc_at },
];

const RIDE_PILL = {
  RC: { label: 'Ride completed', className: 'bg-ui-blue-bg text-ui-blue-fg', dot: 'bg-ui-blue-fg' },
  POB: { label: 'Passenger on board', className: 'bg-ui-green-bg text-ui-green-fg', dot: 'bg-ui-green-fg' },
  VOL: { label: 'Chauffeur on location', className: 'bg-ui-green-bg text-ui-green-fg', dot: 'bg-ui-green-fg' },
  CA: { label: 'Chauffeur assigned', className: 'bg-ui-indigo-bg text-ui-indigo-fg', dot: 'bg-ui-indigo-fg' },
  CNC: { label: 'Cancelled', className: 'bg-ui-red-bg text-ui-red-fg', dot: 'bg-ui-red-fg' },
};

const stampTime = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const RideProgress = ({ cd }) => {
  const doneCount = RIDE_STEPS.filter(s => s.done(cd)).length;
  const current = doneCount - 1;
  return (
    <ol className="m-0 list-none flex items-start gap-0 px-4 sm:px-[22px] pt-[18px] pb-1 overflow-x-auto" aria-label={`Ride progress: ${RIDE_STEPS[Math.max(current, 0)].label.toLowerCase()}`}>
      {RIDE_STEPS.map((step, i) => {
        const done = i <= current;
        const at = done ? stampTime(step.at(cd)) : null;
        return (
          <React.Fragment key={step.label}>
            {i > 0 && <li aria-hidden="true" className={`flex-1 min-w-[16px] h-0.5 mt-[5px] ${i <= current ? 'bg-ui-green-fg' : 'bg-ui-line'}`} />}
            <li className={`flex flex-col items-center gap-1 text-center text-xs min-w-[64px] ${i === current ? 'text-ui-ink font-semibold' : done ? 'text-ui-ink-2' : 'text-ui-muted'}`}>
              <span className={`w-3 h-3 rounded-full border-2 ${i === current ? 'border-[3px] border-ui-green-fg' : done ? 'bg-ui-green-fg border-ui-green-fg' : 'border-ui-control bg-ui-surface'}`} />
              {step.label}
              {at && <span className="text-[11px] text-ui-faint font-medium" style={MONO}>{at}</span>}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
};

const ChauffeurCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = dataOf(component);
  const pickup = present(cd.pickup_location) || 'Pickup';
  const dropoff = present(cd.dropoff_location) || 'Drop-off';
  const cancelled = cd.urbanride_status === 'CNC' || displayStatus(component) === 'cancelled';
  const pill = cancelled ? RIDE_PILL.CNC : RIDE_PILL[cd.vehicle_status] || (cd.chauffeur_status === 'CA' ? RIDE_PILL.CA : null);
  const urcn = present(cd.urbanride_urcn || cd.urbanride_booking_urcn);
  const people = peopleOn(cd, trip, 'passengers');
  const stops = Array.isArray(cd.stops) ? cd.stops : [];
  const nameBoard = present(cd.name_board || cd.name_board_sign || cd.sign_text);
  const flightNote = (code, airline, number) => [code, [airline, number].filter(Boolean).join(' ')].filter(Boolean).join(' · ');

  return (
    <CardShell label={`Chauffeured transfer to ${dropoff}`}>
      <CardHeader
        Icon={Car}
        tone="indigo"
        title={`Transfer to ${dropoff}`}
        subtitle={['Urbanride', present(cd.vehicle_class), formatDay(wallClock(component.start_datetime)?.date)].filter(Boolean).join(' · ')}
      >
        {pill ? <Pill className={pill.className} dot={pill.dot}>{pill.label}</Pill> : <StatusPill status={displayStatus(component)} />}
      </CardHeader>
      {!cancelled && <RideProgress cd={cd} />}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] items-center gap-3 px-4 sm:px-[22px] py-[18px]">
        <TimeBlock
          label="Pickup"
          time={wallClock(component.start_datetime)?.time}
          prefs={prefs}
          below={<>
            <div className="text-sm font-semibold text-ui-ink mt-1 break-words">{pickup}</div>
            {present(cd.pickup_address) && cd.pickup_address !== pickup && <div className="text-[13px] text-ui-ink-3 break-words">{cd.pickup_address}</div>}
            {cd.pickup_type === 'airport' && <div className="text-[13px] text-ui-ink-3">{flightNote(cd.pickup_airport_code, cd.pickup_airline_code, cd.pickup_flight_number)}</div>}
          </>}
        />
        <ArrowRight className="hidden sm:block w-5 h-5 text-ui-indigo-fg" aria-hidden="true" />
        <TimeBlock
          label="Drop-off"
          align="right"
          muted
          time={wallClock(component.end_datetime)?.time}
          prefs={prefs}
          below={<>
            <div className="text-sm font-semibold text-ui-ink mt-1 break-words">{dropoff}</div>
            {present(cd.dropoff_address) && cd.dropoff_address !== dropoff && <div className="text-[13px] text-ui-ink-3 break-words">{cd.dropoff_address}</div>}
            {cd.dropoff_type === 'airport' && <div className="text-[13px] text-ui-ink-3">{flightNote(cd.dropoff_airport_code, cd.dropoff_airline_code, cd.dropoff_flight_number)}</div>}
          </>}
        />
      </div>
      {present(cd.chauffeur_name) && (
        <div className="mx-4 sm:mx-[22px] mb-[18px] flex flex-wrap items-center justify-between gap-3 px-3.5 py-3 rounded-[10px] bg-ui-sunk border border-ui-divider">
          <div className="flex items-center gap-3 min-w-0">
            <div aria-hidden="true" className="w-10 h-10 rounded-full flex items-center justify-center bg-ui-indigo-bg text-ui-indigo-fg text-sm font-bold flex-none">{initials(cd.chauffeur_name)}</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ui-ink">{cd.chauffeur_name} · your chauffeur</div>
              {present(cd.vehicle_plate) && <div className="text-[13px] text-ui-ink-3">{present(cd.vehicle_description) ? `${cd.vehicle_description} · ` : ''}Plate <span style={MONO}>{cd.vehicle_plate}</span></div>}
            </div>
          </div>
          {present(cd.chauffeur_mobile) && (
            <ButtonLink href={`tel:${String(cd.chauffeur_mobile).replace(/[^\d+]/g, '')}`} label={`Call chauffeur ${cd.chauffeur_name}`}>
              <Phone className="w-3.5 h-3.5" aria-hidden="true" />{cd.chauffeur_mobile}
            </ButtonLink>
          )}
        </div>
      )}
      <DetailGrid items={[
        people.length > 0 && { term: people.length === 1 ? 'Passenger' : 'Passengers', children: <PeopleLines people={people} /> },
        nameBoard && { term: 'Name board', children: <DetailText><span style={MONO}>{nameBoard}</span></DetailText> },
        { term: 'Stops', children: <DetailText>{stops.length ? stops.map(s => s.location || s.location_address).filter(Boolean).join(' · ') : 'Direct, no stops'}</DetailText> },
      ]} />
      <Notes p={p} />
      <Footer
        p={p}
        noun="ride"
        confLabel="Ride"
        confNumber={urcn}
        onEdit={p.onQuickEdit || p.onEdit}
        editLabel="Modify ride"
        editText="Modify ride"
        deleteLabel="Cancel ride"
        deleteText="Cancel ride"
        DeleteIcon={X}
      />
    </CardShell>
  );
};

// ── Rental car: pick-up and return ────────────────────────────────────────
const rentalDays = (component, cd) => {
  const pick = wallClock(component.originalComponent?.start_datetime || cd.pickup_datetime || cd.pickup_date);
  const drop = wallClock(component.originalComponent?.end_datetime || cd.dropoff_datetime || cd.dropoff_date);
  if (!pick || !drop) return null;
  const days = Math.round((Date.parse(`${drop.date}T00:00:00Z`) - Date.parse(`${pick.date}T00:00:00Z`)) / 86400000);
  return days > 0 ? days : null;
};

const rentalNames = (cd, title) => ({
  company: present(cd.rental_company || cd.rental_car_company) || title?.replace(/^(Pickup|Return):\s*/, '') || 'Rental car',
  vehicle: present(cd.vehicle_type || cd.car_type),
});

const RentalPickupCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = component.rentalData || dataOf(component);
  const { company, vehicle } = rentalNames(cd, component.title);
  const pickup = present(cd.pickup_location || component.location);
  const dropoff = present(cd.dropoff_location) || pickup;
  const days = rentalDays(component, cd);
  const returnRaw = component.originalComponent?.end_datetime || cd.dropoff_datetime;
  const drivers = peopleOn(cd, trip, 'drivers');

  return (
    <CardShell label={`Rental car pick-up: ${company}${pickup ? ` at ${pickup}` : ''}`}>
      <CardHeader Icon={CarFront} tone="amber" title={[company, vehicle].filter(Boolean).join(' · ')} subtitle={pickup}>
        <StatusPill status={displayStatus(component)} />
        {days && <span className="text-[13px] font-semibold text-ui-ink-2">{days} day{days === 1 ? '' : 's'}</span>}
      </CardHeader>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-4 px-4 sm:px-[22px] py-5">
        <TimeBlock label="Pick-up" time={wallClock(component.start_datetime)?.time} prefs={prefs} below={<DateLine raw={component.start_datetime} />} />
        <RouteLine tone="amber" Icon={KeyRound} caption={dropoff && pickup && dropoff !== pickup ? 'One way' : 'Same location'} />
        <TimeBlock label="Return" align="right" time={wallClock(returnRaw)?.time} prefs={prefs} below={<DateLine raw={returnRaw} />} />
      </div>
      <DetailGrid items={[
        pickup && { term: 'Pick-up desk', children: <><DetailText>{pickup}</DetailText><Directions query={`${company}, ${pickup}`} /></> },
        dropoff && dropoff !== pickup && { term: 'Return to', children: <DetailText>{dropoff}</DetailText> },
        drivers.length > 0 && { term: drivers.length === 1 ? 'Driver' : 'Drivers', children: <PeopleLines people={drivers} /> },
        present(cd.loyalty_program || cd.loyalty_number) && { term: 'Loyalty', children: (
          <DetailText>
            {present(cd.loyalty_program)}
            {present(cd.loyalty_program) && present(cd.loyalty_number) && ' · '}
            {present(cd.loyalty_number) && <span style={MONO}>{`••• ${String(cd.loyalty_number).slice(-4)}`}</span>}
          </DetailText>
        ) },
      ]} />
      <Notes p={p} />
      <Footer p={p} noun="rental car" confNumber={present(cd.confirmation_number || component.confirmation_number)} />
    </CardShell>
  );
};

const RentalReturnCard = (p) => {
  const { component, prefs } = p;
  const cd = component.rentalData || dataOf(component);
  const { company, vehicle } = rentalNames(cd, component.title);
  const place = present(cd.dropoff_location || component.location);
  const days = rentalDays(component, cd);
  const pickupDate = wallClock(component.originalComponent?.start_datetime || cd.pickup_datetime)?.date;
  const next = describeStop(p.nextStop);

  return (
    <CardShell label={`Rental car return: ${company}${place ? ` at ${place}` : ''}`}>
      <div className="flex items-center justify-between gap-3 px-4 sm:px-[22px] py-[18px] border-b border-ui-divider">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-none bg-ui-amber-bg text-ui-amber-fg"><KeyRound className="w-5 h-5" aria-hidden="true" /></div>
          <div className="min-w-0">
            <h3 className="m-0 text-lg font-bold tracking-tight text-ui-ink break-words">Return {[company, vehicle].filter(Boolean).join(' ')}{place ? ` at ${place}` : ''}</h3>
            <div className="text-[13px] text-ui-muted mt-0.5">{days ? `End of a ${days}-day rental` : 'End of rental'}{pickupDate ? ` from ${formatDay(pickupDate)}` : ''}</div>
          </div>
        </div>
        <div className="text-right flex-none">
          <div className="text-[22px] sm:text-[26px] font-semibold text-ui-ink" style={MONO}><TimeText raw={component.start_datetime} prefs={prefs} /></div>
          <div className="text-xs text-ui-muted">Return by</div>
        </div>
      </div>
      {next && (
        <div className="px-4 sm:px-[22px] py-[18px]">
          <InfoStrip Icon={ArrowRight} title={`Next: ${next.title}`} detail={next.detail} />
        </div>
      )}
      <Notes p={p} />
      <Footer p={p} noun="rental car" confNumber={present(cd.confirmation_number || component.confirmation_number)} onDelete={undefined} />
    </CardShell>
  );
};

// ── Restaurant ────────────────────────────────────────────────────────────
const RestaurantCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = dataOf(component);
  const name = present(cd.restaurant_name) || component.title || 'Restaurant';
  const address = present(cd.address || component.location);
  const people = peopleOn(cd, trip, 'restaurant_attendees');
  const party = Number(cd.party_size) || people.length;

  return (
    <CardShell label={`Dinner reservation: ${name}`}>
      <CardHeader Icon={Utensils} tone="orange" title={name} subtitle={[present(cd.cuisine), address].filter(Boolean).join(' · ')}>
        <StatusPill status={displayStatus(component)} />
        {party > 0 && <span className="text-[13px] font-semibold text-ui-ink-2">Party of {party}</span>}
      </CardHeader>
      <div className="grid grid-cols-2 gap-4 px-4 sm:px-[22px] py-5">
        <TimeBlock label="Reservation" time={wallClock(component.start_datetime)?.time} prefs={prefs} below={<DateLine raw={component.start_datetime} />} />
        {present(cd.reservation_holder_name) && (
          <div className="min-w-0 text-right">
            <Label>Held under</Label>
            <div className="text-[15px] font-semibold text-ui-ink mt-2 break-words">{cd.reservation_holder_name}</div>
            {present(cd.phone) && <div className="mt-0.5"><PhoneLink phone={cd.phone} className="text-[13px]" /></div>}
          </div>
        )}
      </div>
      <RideSection>
        <RideTo ride={p.rideTo} bookedTitle="Ride to dinner booked" place="dinner" fromStop={p.prevStop?.title} onBook={p.onBookTo} />
        {p.rideFrom && <TransferBooked booking={p.rideFrom} title="Ride after dinner booked" />}
      </RideSection>
      <DetailGrid items={[
        people.length > 0 && { term: 'Attendees', children: <Chips names={people} /> },
        address && { term: 'Address', children: <><DetailText>{address}</DetailText><Directions query={`${name}, ${address}`} /></> },
      ]} />
      <Notes p={p} />
      <Footer p={p} noun="restaurant" confNumber={present(cd.confirmation_number || component.confirmation_number)} />
    </CardShell>
  );
};

// ── Meeting ───────────────────────────────────────────────────────────────
const MeetingCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = dataOf(component);
  const location = present(component.location);
  const people = peopleOn(cd, trip, cd.meeting_attendees ? 'meeting_attendees' : 'attendees');
  const link = present(cd.meeting_link || cd.virtual_link);
  const duration = durationLabel(component.start_datetime, component.end_datetime);

  return (
    <CardShell label={`Meeting: ${component.title || 'Meeting'}`}>
      <CardHeader Icon={Users} tone="blue" title={component.title || 'Meeting'} subtitle={[cd.is_virtual ? 'Virtual' : 'In person', location].filter(Boolean).join(' · ')}>
        <StatusPill status={displayStatus(component)} />
        {duration && <span className="text-[13px] font-semibold text-ui-ink-2">{duration}</span>}
      </CardHeader>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-4 px-4 sm:px-[22px] py-5">
        <TimeBlock label="Starts" time={wallClock(component.start_datetime)?.time} prefs={prefs} below={<DateLine raw={component.start_datetime} />} />
        <RouteLine tone="blue" />
        <TimeBlock label="Ends" align="right" time={wallClock(component.end_datetime)?.time} prefs={prefs} below={<DateLine raw={component.end_datetime} />} />
      </div>
      {!cd.is_virtual && (
        <RideSection>
          <RideTo ride={p.rideTo} bookedTitle="Ride to the meeting booked" place="the meeting" fromStop={p.prevStop?.title} onBook={p.onBookTo} />
          {p.rideFrom && <TransferBooked booking={p.rideFrom} title="Ride after the meeting booked" />}
        </RideSection>
      )}
      <DetailGrid items={[
        people.length > 0 && { term: 'Attendees', children: <Chips names={people} /> },
        (link || present(cd.dial_in)) && { term: 'Dial-in', children: <>
          {link && <dd className="m-0 mt-1 text-[13px]"><a href={link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-ui-link hover:text-ui-blue-fg">Join video call</a></dd>}
          {present(cd.dial_in) && <DetailText soft>{cd.dial_in}</DetailText>}
        </> },
      ]} />
      <Notes p={p} />
      <Footer p={p} noun="meeting" confNumber={present(cd.confirmation_number)} />
    </CardShell>
  );
};

// ── Speaker meeting ───────────────────────────────────────────────────────
const SpeakerMeetingCard = (p) => {
  const { component, prefs } = p;
  const cd = dataOf(component);
  const venue = present(cd.venue_name || component.location);
  const speaker = present(cd.speaker_name);
  const title = component.title || present(cd.product_name) || 'Speaker meeting';

  return (
    <CardShell label={`Speaker meeting: ${title}`}>
      <CardHeader Icon={Mic} tone="indigo" title={title} subtitle={['Speaker meeting', cd.is_virtual ? 'Virtual' : venue].filter(Boolean).join(' · ')}>
        <StatusPill status={displayStatus(component)} />
        <span className="text-[13px] font-semibold text-ui-ink-2" style={MONO}>
          <TimeText raw={component.start_datetime} prefs={prefs} />
          {wallClock(component.end_datetime)?.time && <> – <TimeText raw={component.end_datetime} prefs={prefs} /></>}
        </span>
      </CardHeader>
      {(speaker || present(cd.sponsor)) && (
        <div className="flex flex-wrap items-center gap-3.5 px-4 sm:px-[22px] py-[18px]">
          {speaker && <div aria-hidden="true" className="w-11 h-11 rounded-full flex items-center justify-center bg-ui-indigo-bg text-ui-indigo-fg text-[15px] font-bold flex-none">{initials(speaker)}</div>}
          <div className="min-w-0 flex-1">
            {speaker && <div className="text-[15px] font-semibold text-ui-ink">{speaker}</div>}
            {present(cd.sponsor) && <div className="text-[13px] text-ui-ink-3">Sponsor: {cd.sponsor}</div>}
          </div>
          {present(cd.venue_address) && (
            <div className="text-right min-w-0">
              <Label>Venue</Label>
              <div className="text-[13px] text-ui-ink mt-1 break-words">{cd.venue_address}</div>
            </div>
          )}
        </div>
      )}
      {!cd.is_virtual && (
        <RideSection>
          <RideTo ride={p.rideTo} bookedTitle="Ride to the venue booked" place="the venue" fromStop={p.prevStop?.title} onBook={p.onBookTo} />
          {p.rideFrom && <TransferBooked booking={p.rideFrom} title="Ride from the venue booked" />}
        </RideSection>
      )}
      <Notes p={p} />
      <Footer p={p} noun="speaker meeting" confNumber={present(cd.confirmation_number)} />
    </CardShell>
  );
};

// ── Event ─────────────────────────────────────────────────────────────────
const EventCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = dataOf(component);
  const venue = present(cd.event_venue_name || cd.venue_name);
  const address = present(cd.event_location || component.location);
  const people = peopleOn(cd, trip, 'attendees');
  const duration = durationLabel(component.start_datetime, component.end_datetime);
  const kind = present(cd.event_type);

  return (
    <CardShell label={`Event: ${component.title || 'Event'}`}>
      <CardHeader Icon={Ticket} tone="pink" title={component.title || 'Event'} subtitle={[kind && kind.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()), venue].filter(Boolean).join(' · ')}>
        <StatusPill status={displayStatus(component)} />
        <span className="text-[13px] font-semibold text-ui-ink-2">{formatDay(wallClock(component.start_datetime)?.date)}</span>
      </CardHeader>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 sm:gap-4 px-4 sm:px-[22px] py-5">
        <TimeBlock label="Doors" time={wallClock(component.start_datetime)?.time} prefs={prefs} />
        <RouteLine tone="pink" caption={duration} />
        <TimeBlock label="Ends" align="right" time={wallClock(component.end_datetime)?.time} prefs={prefs} />
      </div>
      <RideSection>
        <RideTo ride={p.rideTo} bookedTitle="Ride to the event booked" place="the event" fromStop={p.prevStop?.title} onBook={p.onBookTo} />
        {p.rideFrom && <TransferBooked booking={p.rideFrom} title="Ride home booked" />}
      </RideSection>
      <DetailGrid items={[
        people.length > 0 && { term: 'Attending', children: <Chips names={people} /> },
        address && { term: 'Venue', children: <><DetailText>{address}</DetailText><Directions query={[venue, address].filter(Boolean).join(', ')} /></> },
      ]} />
      <Notes p={p} />
      <Footer p={p} noun="event" confLabel="Ticket" confNumber={present(cd.ticket_number || cd.confirmation_number)} />
    </CardShell>
  );
};

// ── Airport VIP service ───────────────────────────────────────────────────
const VipServiceCard = (p) => {
  const { component, prefs, trip } = p;
  const cd = dataOf(component);
  const direction = present(cd.service_direction)?.toLowerCase();
  const dirLabel = direction === 'departure' ? 'Departure' : direction === 'arrival' ? 'Arrival' : direction === 'connection' ? 'Connection' : null;
  const terminal = present(cd.terminal);
  const where = [present(cd.airport_code), terminal && (/^terminal/i.test(terminal) ? terminal : `Terminal ${terminal}`)].filter(Boolean).join(' ');
  const people = peopleOn(cd, trip, 'passengers');

  return (
    <CardShell label={`Airport VIP ${dirLabel ? `${dirLabel.toLowerCase()} ` : ''}service${where ? ` at ${where}` : ''}`}>
      <CardHeader Icon={ConciergeBell} tone="teal" title={[`VIP ${dirLabel ? dirLabel.toLowerCase() : 'service'}`, where].filter(Boolean).join(' · ')} subtitle={['Meet & assist', present(cd.provider_name)].filter(Boolean).join(' · ')}>
        <StatusPill status={displayStatus(component)} />
        {dirLabel && <Pill className="bg-ui-teal-bg text-ui-teal-fg">{dirLabel}</Pill>}
      </CardHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 sm:px-[22px] py-5">
        <TimeBlock
          label="Greeter meets"
          time={wallClock(component.start_datetime)?.time}
          prefs={prefs}
          below={<div className="text-[13px] text-ui-ink-2 mt-0.5">{[formatDay(wallClock(component.start_datetime)?.date), present(cd.greet_location) && `at ${cd.greet_location}`].filter(Boolean).join(' · ')}</div>}
        />
        {present(cd.linked_segment_label) && (
          <div className="self-center">
            <InfoStrip Icon={PlaneLanding} title={`For ${cd.linked_segment_label}`} />
          </div>
        )}
      </div>
      {(present(cd.greeter_name) || present(cd.name_board_sign)) && (
        <div className="grid grid-cols-2 gap-4 mx-4 sm:mx-[22px] mb-[18px] px-3.5 py-3 rounded-[10px] bg-ui-sunk border border-ui-divider">
          {present(cd.greeter_name) && (
            <div className="min-w-0">
              <Label>Greeter</Label>
              <div className="text-sm font-semibold text-ui-ink mt-1">{cd.greeter_name}</div>
              <PhoneLink phone={cd.greeter_phone} className="text-[13px]" />
            </div>
          )}
          {present(cd.name_board_sign) && (
            <div className="min-w-0">
              <Label>Name board reads</Label>
              <div className="text-sm font-semibold text-ui-ink mt-1" style={MONO}>{cd.name_board_sign}</div>
            </div>
          )}
        </div>
      )}
      <DetailGrid items={[
        people.length > 0 && { term: people.length === 1 ? 'Passenger' : 'Passengers', children: <PeopleLines people={people} /> },
        present(cd.provider_name) && { term: 'Provider', children: <><DetailText>{cd.provider_name}</DetailText>{present(cd.provider_phone) && <dd className="m-0 mt-0.5"><PhoneLink phone={cd.provider_phone} /></dd>}</> },
      ]} />
      <Notes p={p} />
      <Footer p={p} noun="VIP service" confNumber={present(cd.confirmation_number || component.confirmation_number)} />
    </CardShell>
  );
};

// ── Memo ──────────────────────────────────────────────────────────────────
const MemoCard = (p) => {
  const { component, trip } = p;
  const cd = dataOf(component);
  const people = peopleOn(cd, trip, 'memo_attendees');
  const content = present(cd.memo_content || component.description);

  return (
    <CardShell label={`Memo: ${component.title || 'Memo'}`}>
      <CardHeader Icon={MemoIcon} tone="amber" title={component.title || 'Memo'} subtitle={['Memo', wallClock(component.start_datetime)?.date && formatDay(wallClock(component.start_datetime).date)].filter(Boolean).join(' · ')} />
      {(content || people.length > 0) && (
        <div className="flex flex-col gap-3.5 px-4 sm:px-[22px] py-[18px]">
          {content && <p className="m-0 text-sm leading-relaxed text-ui-ink-2 max-w-[60ch] whitespace-pre-line">{content}</p>}
          {people.length > 0 && <dl className="m-0"><dt className="sr-only">For</dt><Chips names={people} /></dl>}
        </div>
      )}
      <Notes p={p} />
      <Footer p={p} noun="memo" />
    </CardShell>
  );
};

const CARDS = {
  private_flight: PrivateFlightCard,
  chauffeur_service: ChauffeurCard,
  rental_pickup: RentalPickupCard,
  rental_return: RentalReturnCard,
  restaurant: RestaurantCard,
  meeting: MeetingCard,
  speaker_meeting: SpeakerMeetingCard,
  event: EventCard,
  airport_vip_service: VipServiceCard,
  memo: MemoCard,
};

/**
 * A designed timeline card for `component`, or null if its type has none.
 * Ride props: rideTo / rideFrom (linked bookings), prevStop / nextStop (the
 * neighbouring stops a ride would come from or go to), onBookTo / onBookFrom.
 * `details` renders above the footer and `footerActions` beside Edit/Delete.
 */
export const TripTimelineCard = (props) => {
  const Card = CARDS[tripCardKind(props.component)];
  return Card ? <Card {...props} /> : null;
};

export default TripTimelineCard;
